import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { getUploadUrl } from '../utils/api';
import AnnotationCanvas from '../components/AnnotationCanvas';
import { ArrowLeft, Save, Download, X, Printer } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const GradeSubmission = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [exam, setExam] = useState(null);
  const [school, setSchool] = useState(null);
  const [annotations, setAnnotations] = useState([]);
  const [partMarks, setPartMarks] = useState([]); // Individual marks for each part
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfFileName, setPdfFileName] = useState('');
  const paperRef = useRef(null);
  const iframeRef = useRef(null);

  // Calculate totals from part marks
  const calculateTotals = () => {
    let total = 0;
    let maxTotal = 0;
    const aoTotals = { AO1: 0, AO2: 0, AO3: 0, AO1_max: 0, AO2_max: 0, AO3_max: 0 };
    
    partMarks.forEach(pm => {
      total += pm.marks_awarded || 0;
      maxTotal += pm.max_marks || 0;
      if (pm.assessment_objective && aoTotals.hasOwnProperty(pm.assessment_objective)) {
        aoTotals[pm.assessment_objective] += pm.marks_awarded || 0;
        aoTotals[`${pm.assessment_objective}_max`] += pm.max_marks || 0;
      }
    });
    
    return { total, maxTotal, percentage: maxTotal > 0 ? (total / maxTotal * 100) : 0, aoTotals };
  };

  // Auto-calculate grade from percentage
  const calculateGrade = (percentage) => {
    if (percentage >= 90) return 'A*';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B';
    if (percentage >= 60) return 'C';
    if (percentage >= 50) return 'D';
    if (percentage >= 40) return 'E';
    if (percentage >= 30) return 'F';
    return 'G';
  };

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
    };
  }, [pdfBlobUrl]);

  useEffect(() => {
    fetchData();
  }, [submissionId]);

  const fetchData = async () => {
    try {
      const [submissionRes, schoolRes] = await Promise.all([
        api.get(`/submissions/${submissionId}`),
        api.get('/school/info')
      ]);
      const examRes = await api.get(`/exams/${submissionRes.data.exam_id}`);
      
      setSubmission(submissionRes.data);
      setExam(examRes.data);
      setSchool(schoolRes.data);
      
      // Initialize part marks and annotations for all question parts
      const initialAnnotations = [];
      const initialPartMarks = [];
      
      examRes.data.questions.forEach((q, qIndex) => {
        if (q.question_type === 'descriptive' || !q.question_type) {
          (q.parts || []).forEach((p, pIndex) => {
            // Check for sub-parts
            if (p.sub_parts && p.sub_parts.length > 0) {
              p.sub_parts.forEach((sp, spIndex) => {
                initialAnnotations.push({
                  question_idx: qIndex,
                  part_idx: pIndex,
                  sub_part_idx: spIndex,
                  annotation_data: null,
                  text_feedback: null,
                });
                initialPartMarks.push({
                  question_idx: qIndex,
                  part_idx: pIndex,
                  sub_part_idx: spIndex,
                  marks_awarded: 0,
                  max_marks: sp.marks || 0,
                  assessment_objective: sp.assessment_objective || null,
                  feedback: '',
                });
              });
            } else {
              // Part without sub-parts
              initialAnnotations.push({
                question_idx: qIndex,
                part_idx: pIndex,
                sub_part_idx: -1,
                annotation_data: null,
                text_feedback: null,
              });
              initialPartMarks.push({
                question_idx: qIndex,
                part_idx: pIndex,
                sub_part_idx: -1,
                marks_awarded: 0,
                max_marks: p.marks || 0,
                assessment_objective: p.assessment_objective || null,
                feedback: '',
              });
            }
          });
        } else if (q.question_type === 'mcq') {
          // MCQ questions - auto-graded, but still track marks
          initialPartMarks.push({
            question_idx: qIndex,
            part_idx: -1,
            sub_part_idx: -1,
            marks_awarded: 0,
            max_marks: q.marks || 0,
            assessment_objective: null,
            feedback: '',
          });
        }
      });
      
      // If already graded, load existing data - but preserve assessment_objective from exam
      if (submissionRes.data.is_graded && submissionRes.data.part_marks) {
        setAnnotations(submissionRes.data.annotations || initialAnnotations);
        
        // Merge saved part_marks with assessment_objective from exam questions
        const mergedPartMarks = submissionRes.data.part_marks.map(savedPm => {
          // Find the matching initial part mark to get the assessment_objective from exam
          const matchingInitial = initialPartMarks.find(
            initPm => 
              initPm.question_idx === savedPm.question_idx &&
              initPm.part_idx === savedPm.part_idx &&
              initPm.sub_part_idx === savedPm.sub_part_idx
          );
          
          return {
            ...savedPm,
            // Use saved assessment_objective if it exists, otherwise get from exam
            assessment_objective: savedPm.assessment_objective || (matchingInitial ? matchingInitial.assessment_objective : null)
          };
        });
        
        setPartMarks(mergedPartMarks);
      } else {
        setAnnotations(initialAnnotations);
        // Pre-fill MCQ marks from auto-grading if available
        if (submissionRes.data.is_mcq_auto_graded && submissionRes.data.mcq_results) {
          const updatedPartMarks = [...initialPartMarks];
          submissionRes.data.mcq_results.forEach(mcq => {
            const pmIndex = updatedPartMarks.findIndex(
              pm => pm.question_idx === mcq.question_idx && pm.part_idx === -1
            );
            if (pmIndex !== -1) {
              updatedPartMarks[pmIndex].marks_awarded = mcq.marks_awarded;
            }
          });
          setPartMarks(updatedPartMarks);
        } else {
          setPartMarks(initialPartMarks);
        }
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      alert('Failed to load submission');
      navigate('/teacher/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateAnnotation = (qIndex, pIndex, annotationData) => {
    setAnnotations((prev) => {
      const newAnnotations = [...prev];
      const annotationIndex = newAnnotations.findIndex(
        (a) => a.question_idx === qIndex && a.part_idx === pIndex
      );
      if (annotationIndex !== -1) {
        newAnnotations[annotationIndex].annotation_data = annotationData;
      }
      return newAnnotations;
    });
  };

  const updateTextFeedback = (qIndex, pIndex, textFeedback) => {
    setAnnotations((prev) => {
      const newAnnotations = [...prev];
      const annotationIndex = newAnnotations.findIndex(
        (a) => a.question_idx === qIndex && a.part_idx === pIndex
      );
      if (annotationIndex !== -1) {
        newAnnotations[annotationIndex].text_feedback = textFeedback;
      }
      return newAnnotations;
    });
  };

  const getTextFeedback = (qIndex, pIndex) => {
    const annotation = annotations.find(
      (a) => a.question_idx === qIndex && a.part_idx === pIndex
    );
    return annotation?.text_feedback || '';
  };

  const getAnswer = (qIndex, pIndex, subPartIdx = -1) => {
    return submission?.answers?.find(
      (a) => a.question_idx === qIndex && a.part_idx === pIndex && (a.sub_part_idx || -1) === subPartIdx
    );
  };

  // Update marks for a specific part
  const updatePartMark = (qIndex, pIndex, subPartIdx, marks) => {
    setPartMarks(prev => {
      const updated = [...prev];
      const idx = updated.findIndex(
        pm => pm.question_idx === qIndex && pm.part_idx === pIndex && pm.sub_part_idx === subPartIdx
      );
      if (idx !== -1) {
        // Ensure marks don't exceed max
        const maxMarks = updated[idx].max_marks;
        updated[idx].marks_awarded = Math.min(Math.max(0, marks), maxMarks);
      }
      return updated;
    });
  };

  // Get marks for a specific part
  const getPartMark = (qIndex, pIndex, subPartIdx = -1) => {
    const pm = partMarks.find(
      p => p.question_idx === qIndex && p.part_idx === pIndex && p.sub_part_idx === subPartIdx
    );
    return pm?.marks_awarded || 0;
  };

  // Get max marks for a specific part
  const getPartMaxMarks = (qIndex, pIndex, subPartIdx = -1) => {
    const pm = partMarks.find(
      p => p.question_idx === qIndex && p.part_idx === pIndex && p.sub_part_idx === subPartIdx
    );
    return pm?.max_marks || 0;
  };

  // Get AO for a specific part
  const getPartAO = (qIndex, pIndex, subPartIdx = -1) => {
    const pm = partMarks.find(
      p => p.question_idx === qIndex && p.part_idx === pIndex && p.sub_part_idx === subPartIdx
    );
    return pm?.assessment_objective || null;
  };

  const handleSave = async () => {
    // Validate that all parts have been graded
    const { total, maxTotal, percentage } = calculateTotals();
    
    if (partMarks.length === 0) {
      toast.error('No parts to grade', { duration: 3000 });
      return;
    }

    setSaving(true);
    const loadingToast = toast.loading('Saving grades...');
    
    try {
      const response = await api.patch(`/submissions/${submissionId}/grade`, {
        part_marks: partMarks,
        annotations,
      });
      
      toast.dismiss(loadingToast);
      toast.success(
        `Grade saved! Total: ${total}/${maxTotal} (${response.data.percentage}%) - Grade: ${response.data.grade}`,
        { duration: 4000 }
      );
      
      setTimeout(() => {
        navigate(`/teacher/submissions/${submission.exam_id}`);
      }, 1500);
    } catch (err) {
      console.error('Failed to save grade:', err);
      toast.dismiss(loadingToast);
      toast.error('Failed to save grade. Please try again.', {
        duration: 4000,
      });
      setSaving(false);
    }
  };

  const downloadPDF = async () => {
    const loadingToast = toast.loading('Generating PDF, please wait...');
    
    try {
      const element = paperRef.current;
      
      if (!element) {
        throw new Error('Paper content not found. Please try again.');
      }
      
      // Wait a bit for any pending renders
      await new Promise(resolve => setTimeout(resolve, 500));
      
      
      // Capture with simpler options
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
        windowWidth: element.scrollWidth,
        windowHeight: element.scrollHeight
      });
      
      
      // Convert to image
      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      
      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Calculate dimensions
      const imgWidth = pdfWidth;
      const imgHeight = (canvas.height * pdfWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 0;
      
      // Add first page
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');
      heightLeft -= pdfHeight;
      
      // Add additional pages if needed
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight, '', 'FAST');
        heightLeft -= pdfHeight;
      }
      
      // Generate filename
      const examTitle = (exam?.title || 'Exam').replace(/[^a-z0-9]/gi, '_');
      const studentName = (submission?.student_name || 'Student').replace(/[^a-z0-9]/gi, '_');
      const fileName = `${examTitle}-${studentName}-Graded.pdf`;
      
      
      // Get PDF as Blob (more reliable for downloads)
      const pdfBlob = pdf.output('blob');
      
      // Create blob URL
      const blobUrl = URL.createObjectURL(pdfBlob);
      
      // Clean up previous blob URL if exists
      if (pdfBlobUrl) {
        URL.revokeObjectURL(pdfBlobUrl);
      }
      
      // Store PDF data and show modal
      setPdfBlobUrl(blobUrl);
      setPdfFileName(fileName);
      setShowPdfModal(true);
      
      toast.dismiss(loadingToast);
      toast.success('✅ PDF generated! Use the preview to print or save.', { duration: 4000 });
      
    } catch (err) {
      console.error('❌ PDF generation failed:', err);
      console.error('Error name:', err.name);
      console.error('Error message:', err.message);
      console.error('Error stack:', err.stack);
      
      toast.dismiss(loadingToast);
      
      // More helpful error messages
      let errorMsg = 'Failed to generate PDF. ';
      if (err.message.includes('not found')) {
        errorMsg += 'Content not ready. Please wait and try again.';
      } else if (err.message.includes('canvas')) {
        errorMsg += 'Problem capturing page. Try refreshing the page.';
      } else {
        errorMsg += err.message || 'Please try again.';
      }
      
      toast.error(errorMsg, { duration: 6000 });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-slate-500">Loading submission...</div>
      </div>
    );
  }

  const totalMarks = exam?.questions?.reduce((sum, q) => sum + q.marks, 0) || 0;

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate(`/teacher/submissions/${submission.exam_id}`)}
            className="flex items-center gap-2 text-slate-600 hover:text-indigo-700 transition-colors mb-2"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Submissions
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Grading - {submission?.student_name}
              </h1>
              <p className="text-slate-600">{exam?.title}</p>
            </div>
            {submission?.is_graded && (
              <button
                onClick={downloadPDF}
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-all"
              >
                <Download className="w-5 h-5" />
                Download PDF
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6 md:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Submission Content */}
          <div ref={paperRef} className="lg:col-span-2 space-y-8">
            
            {/* School Header for PDF */}
            {school && (
              <div className="bg-white rounded-xl border-2 border-slate-200 p-6 flex items-center gap-4">
                {school.logo_url && (
                  <img src={getUploadUrl(school.logo_url)} alt={school.name} className="w-16 h-16 rounded-lg object-cover" />
                )}
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">{school.name}</h1>
                  <p className="text-slate-600">Graded Examination Paper</p>
                </div>
              </div>
            )}
            
            {/* MCQ Auto-Graded Results Summary */}
            {submission?.is_mcq_auto_graded && submission?.mcq_results && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border-2 border-green-200 p-6">
                <h2 className="text-xl font-bold text-green-800 mb-4">MCQ Auto-Graded Results</h2>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-600">Score</p>
                    <p className="text-3xl font-bold text-green-600">{submission.mcq_score}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-600">Total MCQ Marks</p>
                    <p className="text-3xl font-bold text-slate-700">{submission.mcq_total}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-sm text-slate-600">Percentage</p>
                    <p className="text-3xl font-bold text-indigo-600">
                      {submission.mcq_total > 0 
                        ? Math.round((submission.mcq_score / submission.mcq_total) * 100)
                        : 0}%
                    </p>
                  </div>
                </div>
                <p className="text-sm text-green-700">
                  The MCQ score is pre-filled in the "Marks Allocated" field. You can adjust it before saving.
                </p>
              </div>
            )}

            {exam?.questions.map((question, qIndex) => {
              const isMCQ = question.question_type === 'mcq';
              const mcqResult = submission?.mcq_results?.find(r => r.question_idx === qIndex);
              
              return (
              <div
                key={qIndex}
                data-testid={`question-${qIndex}`}
                className={`bg-white rounded-xl border-2 p-8 ${
                  isMCQ 
                    ? mcqResult?.is_correct 
                      ? 'border-green-300' 
                      : 'border-red-300'
                    : 'border-slate-200'
                }`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h2 className="text-2xl font-semibold text-slate-900">
                      Question {qIndex + 1}
                    </h2>
                    {isMCQ && (
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        mcqResult?.is_correct 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {mcqResult?.is_correct ? '✓ Correct' : '✗ Wrong'}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-indigo-100 text-indigo-700 font-medium rounded-full text-sm">
                      {question.marks} marks
                    </span>
                    {isMCQ && mcqResult && (
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        mcqResult.marks_awarded >= 0 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-red-100 text-red-700'
                      }`}>
                        {mcqResult.marks_awarded >= 0 ? '+' : ''}{mcqResult.marks_awarded}
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-lg text-slate-700 mb-6">{question.question_text}</p>

                {/* MCQ Question Display */}
                {isMCQ && (
                  <>
                    {/* Question Diagram */}
                    {question.question_diagram && (
                      <div className="mb-6">
                        <img
                          src={question.question_diagram}
                          alt="Question diagram"
                          className="max-w-full rounded-lg border border-slate-200"
                        />
                      </div>
                    )}

                    {/* MCQ Options with Results */}
                    <div className="grid grid-cols-2 gap-3">
                      {question.mcq_options?.map((opt, optIdx) => {
                        const isCorrect = optIdx === mcqResult?.correct_option;
                        const isSelected = optIdx === mcqResult?.selected_option;
                        
                        return (
                          <div
                            key={optIdx}
                            className={`p-4 rounded-lg border-2 ${
                              isCorrect
                                ? 'border-green-500 bg-green-50'
                                : isSelected && !isCorrect
                                ? 'border-red-500 bg-red-50'
                                : 'border-slate-200 bg-white'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              <span className="font-bold text-slate-700">
                                {String.fromCharCode(65 + optIdx)}.
                              </span>
                              <div className="flex-1">
                                {opt.option_text && (
                                  <span className="text-slate-700">{opt.option_text}</span>
                                )}
                                {opt.option_image && (
                                  <img
                                    src={opt.option_image}
                                    alt={`Option ${String.fromCharCode(65 + optIdx)}`}
                                    className="mt-2 max-h-24 rounded border border-slate-200"
                                  />
                                )}
                              </div>
                              <div className="flex-shrink-0">
                                {isCorrect && (
                                  <span className="text-green-600 text-sm font-medium">✓ Correct</span>
                                )}
                                {isSelected && !isCorrect && (
                                  <span className="text-red-600 text-sm font-medium">Student's answer</span>
                                )}
                                {isSelected && isCorrect && (
                                  <span className="text-green-600 text-sm font-medium">✓ Student's answer</span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {mcqResult?.selected_option === null && (
                      <p className="mt-4 text-amber-600 text-sm font-medium">Not answered by student</p>
                    )}
                  </>
                )}

                {/* Descriptive Parts */}
                {(!isMCQ && question.parts) && (
                <div className="space-y-6">
                  {question.parts.map((part, pIndex) => {
                    const answer = getAnswer(qIndex, pIndex);
                    const hasSubParts = part.sub_parts && part.sub_parts.length > 0;
                    
                    return (
                      <div
                        key={pIndex}
                        data-testid={`part-${qIndex}-${pIndex}`}
                        className="p-6 bg-slate-50 rounded-lg border border-slate-200"
                      >
                        <h3 className="font-semibold text-slate-900 mb-4">
                          Part {String.fromCharCode(97 + pIndex)}) {part.part_text}
                        </h3>

                        {/* Show diagram for parts that don't require drawing */}
                        {part.diagram_base64 && !part.has_drawing && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Reference Diagram:
                            </label>
                            <img
                              src={part.diagram_base64}
                              alt="Reference diagram"
                              className="max-w-full rounded-lg border border-slate-200"
                            />
                          </div>
                        )}

                        {/* Student's Drawing with Annotation Canvas */}
                        {answer?.drawing_data && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                              Student's Drawing (Add Annotations):
                            </label>
                            <AnnotationCanvas
                              backgroundImage={answer.drawing_data}
                              initialAnnotation={annotations.find(
                                (a) => a.question_idx === qIndex && a.part_idx === pIndex
                              )?.annotation_data}
                              onSave={(annotationData) =>
                                updateAnnotation(qIndex, pIndex, annotationData)
                              }
                            />
                          </div>
                        )}

                        {/* Show diagram for annotation when part has_drawing but student didn't draw */}
                        {part.has_drawing && part.diagram_base64 && !answer?.drawing_data && (
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-amber-600 mb-2">
                              Reference Diagram (Student did not submit drawing - Annotate here):
                            </label>
                            <AnnotationCanvas
                              backgroundImage={part.diagram_base64}
                              initialAnnotation={annotations.find(
                                (a) => a.question_idx === qIndex && a.part_idx === pIndex
                              )?.annotation_data}
                              onSave={(annotationData) =>
                                updateAnnotation(qIndex, pIndex, annotationData)
                              }
                            />
                          </div>
                        )}

                        {/* Message when no drawing submitted and no diagram available */}
                        {part.has_drawing && !part.diagram_base64 && !answer?.drawing_data && (
                          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-amber-700 text-sm">
                              Student did not submit a drawing for this part.
                            </p>
                          </div>
                        )}

                        {/* Student's Text Answer (for parts without sub-parts) */}
                        {!hasSubParts && answer?.text_answer && (
                          <div className="space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-2">
                                Student's Answer:
                              </label>
                              <div className="p-4 bg-white rounded-lg border border-slate-200">
                                <p className="text-slate-700 whitespace-pre-wrap">
                                  {answer.text_answer}
                                </p>
                              </div>
                            </div>
                            
                            {/* Teacher's Feedback for Text Answer */}
                            <div>
                              <label className="block text-sm font-medium text-red-600 mb-2">
                                Teacher's Feedback:
                              </label>
                              <textarea
                                value={getTextFeedback(qIndex, pIndex)}
                                onChange={(e) => updateTextFeedback(qIndex, pIndex, e.target.value)}
                                placeholder="Add your feedback, corrections, or comments here..."
                                className="w-full h-24 p-3 rounded-lg border-2 border-red-200 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-200 resize-none text-sm"
                              />
                            </div>
                          </div>
                        )}

                        {/* Part Marks Input (only when no sub-parts) */}
                        {!hasSubParts && (
                          <div className="mt-4 pt-4 border-t border-slate-200">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <label className="text-sm font-medium text-slate-700">
                                  Marks for Part {String.fromCharCode(97 + pIndex)}:
                                </label>
                                {getPartAO(qIndex, pIndex, -1) && (
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                                    getPartAO(qIndex, pIndex, -1) === 'AO1' ? 'bg-blue-100 text-blue-700' :
                                    getPartAO(qIndex, pIndex, -1) === 'AO2' ? 'bg-green-100 text-green-700' :
                                    'bg-purple-100 text-purple-700'
                                  }`}>
                                    {getPartAO(qIndex, pIndex, -1)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  max={getPartMaxMarks(qIndex, pIndex, -1)}
                                  step="0.5"
                                  value={getPartMark(qIndex, pIndex, -1)}
                                  onChange={(e) => updatePartMark(qIndex, pIndex, -1, parseFloat(e.target.value) || 0)}
                                  className="w-20 h-10 px-3 text-center rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                  data-testid={`marks-${qIndex}-${pIndex}`}
                                />
                                <span className="text-slate-500">/ {getPartMaxMarks(qIndex, pIndex, -1)}</span>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Sub-Parts (when present) */}
                        {hasSubParts && (
                          <div className="mt-4 space-y-4">
                            {part.sub_parts.map((subPart, spIndex) => {
                              const subPartAnswer = submission?.answers?.find(
                                a => a.question_idx === qIndex && a.part_idx === pIndex && a.sub_part_idx === spIndex
                              );
                              const romanNumerals = ['i', 'ii', 'iii', 'iv', 'v', 'vi', 'vii', 'viii', 'ix', 'x'];
                              const subPartLabel = subPart.sub_part_label || romanNumerals[spIndex] || `${spIndex + 1}`;
                              
                              return (
                                <div 
                                  key={spIndex}
                                  data-testid={`subpart-${qIndex}-${pIndex}-${spIndex}`}
                                  className="ml-4 p-4 bg-white rounded-lg border border-slate-200"
                                >
                                  <div className="flex items-start justify-between mb-3">
                                    <h4 className="font-medium text-slate-800">
                                      ({subPartLabel}) {subPart.sub_part_text}
                                    </h4>
                                    <span className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                                      {subPart.marks} marks
                                    </span>
                                  </div>

                                  {/* Sub-part diagram */}
                                  {subPart.diagram_base64 && (
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-slate-600 mb-1">
                                        Reference Diagram:
                                      </label>
                                      <img
                                        src={subPart.diagram_base64}
                                        alt="Sub-part diagram"
                                        className="max-w-full max-h-48 rounded border border-slate-200"
                                      />
                                    </div>
                                  )}

                                  {/* Student's answer for sub-part */}
                                  {subPartAnswer?.text_answer && (
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-slate-600 mb-1">
                                        Student's Answer:
                                      </label>
                                      <div className="p-3 bg-slate-50 rounded border border-slate-200">
                                        <p className="text-sm text-slate-700 whitespace-pre-wrap">
                                          {subPartAnswer.text_answer}
                                        </p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Student's drawing for sub-part */}
                                  {subPartAnswer?.drawing_data && (
                                    <div className="mb-3">
                                      <label className="block text-xs font-medium text-slate-600 mb-1">
                                        Student's Drawing:
                                      </label>
                                      <img
                                        src={subPartAnswer.drawing_data}
                                        alt="Student drawing"
                                        className="max-w-full max-h-48 rounded border border-slate-200"
                                      />
                                    </div>
                                  )}

                                  {/* Marks input for sub-part */}
                                  <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                                    <div className="flex items-center gap-2">
                                      <label className="text-sm font-medium text-slate-700">
                                        Marks for ({subPartLabel}):
                                      </label>
                                      {subPart.assessment_objective && (
                                        <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                                          subPart.assessment_objective === 'AO1' ? 'bg-blue-100 text-blue-700' :
                                          subPart.assessment_objective === 'AO2' ? 'bg-green-100 text-green-700' :
                                          'bg-purple-100 text-purple-700'
                                        }`}>
                                          {subPart.assessment_objective}
                                        </span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min="0"
                                        max={subPart.marks}
                                        step="0.5"
                                        value={getPartMark(qIndex, pIndex, spIndex)}
                                        onChange={(e) => updatePartMark(qIndex, pIndex, spIndex, parseFloat(e.target.value) || 0)}
                                        className="w-16 h-8 px-2 text-center text-sm rounded border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                        data-testid={`marks-${qIndex}-${pIndex}-${spIndex}`}
                                      />
                                      <span className="text-sm text-slate-500">/ {subPart.marks}</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
            })}
          </div>

          {/* Right: Grading Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-slate-200 p-6 sticky top-6">
              <h2 className="text-xl font-semibold text-slate-900 mb-6">Grade Summary</h2>
              
              {/* Auto-calculated totals */}
              {(() => {
                const { total, maxTotal, percentage, aoTotals } = calculateTotals();
                const autoGrade = calculateGrade(percentage);
                return (
                  <div className="space-y-6">
                    {/* Total Marks */}
                    <div className="p-4 bg-indigo-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-indigo-700">Total Marks</span>
                        <span data-testid="total-marks" className="text-2xl font-bold text-indigo-900">
                          {total} / {maxTotal}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-indigo-600">Percentage</span>
                        <span className="text-lg font-semibold text-indigo-800">
                          {percentage.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-sm text-indigo-600">Auto Grade</span>
                        <span className="text-xl font-bold text-indigo-900">
                          {autoGrade}
                        </span>
                      </div>
                    </div>

                    {/* AO Breakdown */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-slate-700">Assessment Objectives</h3>
                      
                      {/* AO1 */}
                      <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                        <div>
                          <span className="text-xs font-medium text-blue-600">AO1</span>
                          <p className="text-xs text-blue-500">Knowledge</p>
                        </div>
                        <div className="text-right">
                          <span data-testid="ao1-marks" className="text-lg font-bold text-blue-700">
                            {aoTotals.AO1} / {aoTotals.AO1_max}
                          </span>
                          <p className="text-xs text-blue-500">
                            {aoTotals.AO1_max > 0 ? ((aoTotals.AO1 / aoTotals.AO1_max) * 100).toFixed(0) : 0}%
                          </p>
                        </div>
                      </div>
                      
                      {/* AO2 */}
                      <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
                        <div>
                          <span className="text-xs font-medium text-green-600">AO2</span>
                          <p className="text-xs text-green-500">Application</p>
                        </div>
                        <div className="text-right">
                          <span data-testid="ao2-marks" className="text-lg font-bold text-green-700">
                            {aoTotals.AO2} / {aoTotals.AO2_max}
                          </span>
                          <p className="text-xs text-green-500">
                            {aoTotals.AO2_max > 0 ? ((aoTotals.AO2 / aoTotals.AO2_max) * 100).toFixed(0) : 0}%
                          </p>
                        </div>
                      </div>
                      
                      {/* AO3 */}
                      <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                        <div>
                          <span className="text-xs font-medium text-purple-600">AO3</span>
                          <p className="text-xs text-purple-500">Analysis</p>
                        </div>
                        <div className="text-right">
                          <span data-testid="ao3-marks" className="text-lg font-bold text-purple-700">
                            {aoTotals.AO3} / {aoTotals.AO3_max}
                          </span>
                          <p className="text-xs text-purple-500">
                            {aoTotals.AO3_max > 0 ? ((aoTotals.AO3 / aoTotals.AO3_max) * 100).toFixed(0) : 0}%
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Time Info */}
                    <div className="pt-4 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Time Taken</span>
                        <span className="text-sm font-medium text-slate-900">
                          {Math.floor((exam?.duration_minutes * 60 - submission?.timer_remaining) / 60)} mins
                        </span>
                      </div>
                    </div>

                    <button
                      data-testid="save-grade-btn"
                      onClick={handleSave}
                      disabled={saving}
                      className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-indigo-700 hover:bg-indigo-800 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                    >
                      <Save className="w-5 h-5" />
                      {saving ? 'Saving...' : 'Save Grades'}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* PDF Preview Modal */}
      {showPdfModal && pdfBlobUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-5xl h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                PDF Preview - {pdfFileName}
              </h3>
              <div className="flex items-center gap-3">
                <a
                  href={pdfBlobUrl}
                  download={pdfFileName}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </a>
                <button
                  onClick={() => window.print()}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print
                </button>
                <button
                  onClick={() => {
                    setShowPdfModal(false);
                    if (pdfBlobUrl) {
                      URL.revokeObjectURL(pdfBlobUrl);
                      setPdfBlobUrl(null);
                    }
                  }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            
            {/* PDF Iframe */}
            <div className="flex-1 p-4 bg-slate-100 overflow-auto">
              <iframe
                ref={iframeRef}
                src={pdfBlobUrl}
                type="application/pdf"
                className="w-full h-full rounded-lg border border-slate-300"
                title="PDF Preview"
              />
            </div>
            
            {/* Modal Footer with Instructions */}
            <div className="p-4 border-t border-slate-200 bg-slate-50">
              <p className="text-sm text-slate-600 text-center">
                <strong>To save:</strong> Click the green "Download PDF" button above.
                <br />
                If that doesn't work, right-click the button and select "Save link as..."
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GradeSubmission;